import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AMOUNTS = [25, 50, 75, 100, 150, 200];

function PageHero() {
  return (
    <section className="relative min-h-[62vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d4ca567d19cfd4eb78525ef550360b95.jpg"
          alt="Gift Cards background"
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 via-transparent to-transparent" />
      </div>
      <div className="absolute top-20 right-24 w-72 h-72 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-52 h-52 bg-brand-purple-light/15 rounded-full blur-2xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-24 pt-44">
        <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Gift Cards
        </span>
        <h1 className="font-heading text-white text-5xl lg:text-7xl font-black mb-5 leading-tight max-w-3xl">
          Give the Gift of <span className="text-brand-yellow">Creativity</span>
        </h1>
        <p className="font-body text-white/70 text-xl max-w-2xl leading-relaxed">
          A Creative Institute gift card is the perfect way to give someone you love the gift of music, arts, and unlimited creative potential.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          {['Perfect for birthdays', 'Ideal for holidays', 'Great for any occasion'].map((tag) => (
            <span key={tag} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 font-body text-sm">
              <i className="ri-gift-2-line text-brand-yellow text-base" />
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const steps = [
    {
      num: '01',
      icon: 'ri-price-tag-3-line',
      title: 'Choose Your Amount',
      desc: 'Pick from our preset denominations or enter a custom amount. Any value from $25 and up.',
    },
    {
      num: '02',
      icon: 'ri-quill-pen-line',
      title: 'Personalize It',
      desc: "Add the recipient's name and a heartfelt message so they know exactly who it's from.",
    },
    {
      num: '03',
      icon: 'ri-send-plane-2-line',
      title: 'We Send It Out',
      desc: "We'll contact you to deliver the gift card digitally or arrange physical pickup at the studio.",
    },
    {
      num: '04',
      icon: 'ri-music-2-line',
      title: 'They Start Creating',
      desc: 'The recipient redeems it toward any program, session, or service at Creative Institute.',
    },
  ];

  return (
    <section ref={ref} className="py-28 bg-white border-b border-gray-100">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="text-center mb-16">
          <span className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            Simple Process
          </span>
          <h2 className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4`}>
            How It Works
          </h2>
          <p className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} font-body text-gray-400 text-base max-w-xl mx-auto`}>
            Getting a gift card is easy. Here&apos;s how it works from start to finish.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} flex flex-col items-center text-center group`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="relative mb-6">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] group-hover:scale-110 transition-transform duration-300">
                  <i className={`${step.icon} text-2xl text-white`} />
                </div>
                <span className="absolute -top-3 -right-3 w-7 h-7 flex items-center justify-center rounded-full bg-brand-yellow text-brand-blue-dark font-heading text-xs font-black">
                  {step.num}
                </span>
              </div>
              <h3 className="font-heading text-brand-blue-dark text-lg font-black mb-2">{step.title}</h3>
              <p className="font-body text-gray-400 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GiftCardFormSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [delivery, setDelivery] = useState<'digital' | 'pickup'>('digital');
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const finalAmount = selectedAmount ?? (customAmount ? parseInt(customAmount, 10) : null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msgTextarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (msgTextarea && msgTextarea.value.length > 500) return;
    if (!finalAmount || finalAmount < 25) return;

    setStatus('loading');
    const body = new URLSearchParams();
    body.append('gift_card_amount', `$${finalAmount}`);
    body.append('delivery_method', delivery);
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (key !== 'custom_amount') body.append(key, value.toString());
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7632vnlpdqvlupfpod0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section ref={ref} className="py-28 bg-gradient-to-br from-[#F0F4FF] via-white to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left — info + preview card */}
          <div className={`scroll-hidden slide-left ${isVisible ? 'scroll-visible' : ''}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-6">
              Customize Your Gift
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4 leading-tight">
              Order a Gift Card
            </h2>
            <p className="font-body text-gray-400 text-base leading-relaxed mb-10">
              Choose any amount starting at $25. Gift cards can be redeemed toward any program, walk-in session, or service at Creative Institute.
            </p>

            {/* Visual gift card preview */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] p-8 aspect-[1.6/1] flex flex-col justify-between max-w-sm">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=abstract%20musical%20art%20pattern%20with%20faint%20gold%20music%20notes%20piano%20keys%20guitar%20strings%20on%20dark%20royal%20blue%20deep%20purple%20background%2C%20elegant%20luxury%20texture%2C%20subtle%20artistic%20overlay%2C%20no%20people%2C%20wide%20shot&width=600&height=380&seq=giftcard-preview-bg-001&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover object-top opacity-15"
                />
              </div>
              <div className="relative z-10 flex items-center justify-between">
                <span className="font-heading text-white text-lg font-black tracking-tight leading-none">
                  Creative<br />Institute
                </span>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-yellow">
                  <i className="ri-gift-2-line text-brand-blue-dark text-lg" />
                </div>
              </div>
              <div className="relative z-10">
                <p className="font-body text-white/50 text-xs uppercase tracking-widest mb-1">Gift Card Value</p>
                <p className="font-heading text-brand-yellow text-5xl font-black">
                  {finalAmount ? `$${finalAmount}` : '$—'}
                </p>
                <p className="font-body text-white/40 text-xs mt-2">Redeemable at Creative Institute LLC · Fayetteville, NC</p>
              </div>
            </div>

            {/* What it covers */}
            <div className="mt-10 p-6 rounded-2xl bg-white border border-[#E0E7FF]">
              <p className="font-heading text-brand-blue-dark text-sm font-black uppercase tracking-widest mb-4">What it covers</p>
              <ul className="space-y-3">
                {[
                  'Walk-in lessons (Piano, Vocals, Drums)',
                  'Monthly program enrollment',
                  'Recording studio sessions',
                  'Off-site & in-home services',
                  'Any Creative Institute program',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 font-body text-sm text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                      <i className="ri-check-line text-xs" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — form */}
          <div className={`scroll-hidden slide-right ${isVisible ? 'scroll-visible' : ''}`}>
            {status === 'success' ? (
              <div className="rounded-3xl bg-white border-2 border-brand-yellow/30 p-12 flex flex-col items-center justify-center text-center gap-5 min-h-[500px]">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-brand-yellow/15 border border-brand-yellow/30">
                  <i className="ri-gift-2-line text-brand-yellow text-4xl" />
                </div>
                <div>
                  <h3 className="font-heading text-brand-blue-dark text-2xl font-black mb-2">Order Received!</h3>
                  <p className="font-body text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">
                    Your gift card order has been submitted. Our team will reach out within 24 hours to confirm and deliver your gift card.
                  </p>
                </div>
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-semibold text-sm cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <i className="ri-home-line" /> Back to Home
                </Link>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-3xl bg-white border border-[#E0E7FF] p-8 md:p-10 flex flex-col gap-6"
              >
                {/* Amount selector */}
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-3">
                    Select Amount <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {AMOUNTS.map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                        className={`py-3 rounded-xl font-heading font-black text-base border-2 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          selectedAmount === amt
                            ? 'bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] border-transparent text-white'
                            : 'bg-white border-gray-200 text-brand-blue-dark hover:border-brand-purple/40'
                        }`}
                      >
                        ${amt}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-gray-400 font-bold text-sm pointer-events-none">$</span>
                    <input
                      type="number"
                      name="custom_amount"
                      min={25}
                      placeholder="Custom amount (min $25)"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                      className="w-full pl-8 pr-4 py-3 rounded-xl border-2 border-dashed border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple/40 transition-colors bg-[#FAFBFF]"
                    />
                  </div>
                </div>

                {/* Delivery method */}
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-3">
                    Delivery Method <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-3">
                    {[
                      { val: 'digital', icon: 'ri-mail-send-line', label: 'Digital (Email)' },
                      { val: 'pickup', icon: 'ri-store-2-line', label: 'Studio Pickup' },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setDelivery(opt.val as 'digital' | 'pickup')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-body text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                          delivery === opt.val
                            ? 'bg-brand-yellow/15 border-brand-yellow text-brand-yellow-dark'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <i className={`${opt.icon} text-base`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Buyer info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Your Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="buyer_name"
                      required
                      placeholder="Jane Smith"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Your Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Recipient&apos;s Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="recipient_name"
                      required
                      placeholder="e.g. Marcus Johnson"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Recipient&apos;s Email
                    </label>
                    <input
                      type="email"
                      name="recipient_email"
                      placeholder="For digital delivery"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors"
                    />
                  </div>
                </div>

                {/* Personal message */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Personal Message <span className="text-gray-300">(optional)</span>
                    </label>
                    <span className={`font-body text-xs ${charCount > 480 ? 'text-red-400' : 'text-gray-300'}`}>{charCount}/500</span>
                  </div>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="e.g. Happy Birthday! Can&apos;t wait to hear you play..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors resize-none"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Your Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(910) 000-0000"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-brand-purple/40 transition-colors"
                  />
                </div>

                {status === 'error' && (
                  <p className="font-body text-red-400 text-sm text-center">Something went wrong — please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || !finalAmount || finalAmount < 25}
                  className="whitespace-nowrap w-full py-4 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-base cursor-pointer hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <><i className="ri-loader-4-line animate-spin text-lg" /> Processing...</>
                  ) : (
                    <><i className="ri-gift-2-line text-lg" /> Order Gift Card{finalAmount ? ` — $${finalAmount}` : ''}</>
                  )}
                </button>

                <p className="font-body text-gray-400 text-xs text-center leading-relaxed">
                  After submitting, our team will contact you within 24 hours to arrange payment and delivery. Gift cards never expire.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQGiftSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [open, setOpen] = useState<number | null>(null);

  const faqs = [
    {
      q: 'Do Creative Institute gift cards expire?',
      a: 'No — Creative Institute gift cards never expire. The recipient can use them whenever they are ready to start their creative journey.',
    },
    {
      q: 'What programs can a gift card be used for?',
      a: 'Gift cards can be applied to any service we offer — walk-in lessons, monthly enrollment plans, recording studio sessions, in-home programs, and off-site services.',
    },
    {
      q: 'How is the gift card delivered?',
      a: 'You can choose digital delivery (sent to the recipient\'s email) or studio pickup. Our team will confirm the details with you after your order is placed.',
    },
    {
      q: 'Is there a minimum gift card amount?',
      a: 'Yes, the minimum gift card value is $25. There is no maximum — you can enter any custom amount.',
    },
    {
      q: 'Can a gift card be used toward multiple sessions?',
      a: 'Absolutely. Gift card balances can be split across multiple sessions or applied toward a monthly enrollment plan.',
    },
    {
      q: 'How do I pay for the gift card?',
      a: 'After you submit your order, our team will reach out within 24 hours to arrange payment. We accept cash, card, and Afterpay.',
    },
  ];

  return (
    <section ref={ref} className="py-24 bg-white border-t border-gray-100">
      <div className="max-w-3xl mx-auto px-8">
        <div className="text-center mb-14">
          <span className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-blue/15 mb-4`}>
            FAQ
          </span>
          <h2 className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-heading text-4xl font-black text-brand-blue-dark`}>
            Gift Card Questions
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={faq.q}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} rounded-2xl border border-gray-100 overflow-hidden transition-all duration-200`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-[#FAFBFF] transition-colors"
              >
                <span className="font-heading text-brand-blue-dark text-base font-bold">{faq.q}</span>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0 transition-transform duration-200 ${open === i ? 'rotate-45' : ''}`}>
                  <i className="ri-add-line text-base" />
                </div>
              </button>
              {open === i && (
                <div className="px-6 pb-5">
                  <p className="font-body text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GiftCTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-[#FFFBEB] to-white border-t border-brand-yellow/20">
      <div className={`scroll-hidden-scale-blur ${isVisible ? 'scroll-visible' : ''} max-w-2xl mx-auto px-8 text-center`}>
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mx-auto mb-6">
          <i className="ri-gift-2-line text-brand-blue-dark text-3xl" />
        </div>
        <h2 className="font-heading text-3xl lg:text-4xl font-black text-brand-blue-dark mb-4">
          Not Sure What to Get Someone?
        </h2>
        <p className="font-body text-gray-500 text-base leading-relaxed mb-8">
          A Creative Institute gift card gives them the freedom to choose exactly what excites them most — from piano lessons to recording studio time.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#order"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 600, behavior: 'smooth' }); }}
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity"
          >
            <i className="ri-gift-2-line" /> Order a Gift Card
          </a>
          <Link
            to="/contact"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-brand-yellow text-brand-yellow-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow hover:text-brand-blue-dark transition-colors"
          >
            <i className="ri-phone-line" /> Have Questions?
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function GiftCardsPage() {
  return (
    <main>
      <PageHero />
      <HowItWorksSection />
      <GiftCardFormSection />
      <FAQGiftSection />
      <GiftCTA />
    </main>
  );
}
